/**
 * Storage Service (Azure Blob Storage)
 *
 * Provides access to storage assets (backgrounds, themes) via the backend
 * proxy, which works even when the storage account has firewall restrictions.
 */

import { logInfo, logError, logWarn } from '@/lib/logger'
import { getAccessToken } from '@/services/auth-service'
import { env } from '@/lib/env'

const CONTEXT = 'StorageService'

const BACKEND_API_URL = env.backendApiUrl
const AZURE_STORAGE_CONTAINER = env.azureStorageContainer

/**
 * Background image metadata
 */
export interface BackgroundImage {
  id: string
  name: string
  path: string
  url: string
  createdAt: string
}

/**
 * Get the backend proxy URL for a file in Azure Blob Storage.
 *
 * @param path - Path to the file within the container (e.g., 'login-background.png')
 * @returns Backend proxy URL for the blob
 */
function getPublicUrl(path: string): string {
  if (!BACKEND_API_URL) {
    logWarn({ context: CONTEXT }, 'Backend API not configured - returning empty URL')
    return ''
  }

  // Remove leading slash if present
  const cleanPath = path.startsWith('/') ? path.slice(1) : path

  return `${BACKEND_API_URL}/api/v1/storage/assets/${AZURE_STORAGE_CONTAINER}/${cleanPath}`
}

/**
 * Fallback when the backend listing is unavailable (Azure Blob Storage has no
 * anonymous listing). Only the neutral login background: customer theme
 * backgrounds carry customer names and would ship in every client's bundle
 * (#1207, see #971).
 */
const PREDEFINED_BACKGROUNDS = [
  { name: 'login-background.png', path: 'login-background.png' },
]

const FALLBACK_BACKGROUND_IMAGES: BackgroundImage[] = PREDEFINED_BACKGROUNDS.map(bg => ({
  id: bg.name.replace(/\.[^.]+$/, '').replace(/-/g, '_'),
  name: bg.name.replace(/\.[^.]+$/, '').replace(/-/g, ' '),
  path: bg.path,
  url: getPublicUrl(bg.path),
  createdAt: '',
}))

export const storageService = {
  getPublicUrl,

  async listImages(): Promise<BackgroundImage[]> {
    // Try to fetch from backend API first
    if (BACKEND_API_URL) {
      try {
        // Admin only since #971 — the file names carry customer names.
        const token = getAccessToken()
        const response = await fetch(`${BACKEND_API_URL}/api/v1/storage/backgrounds`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
        if (response.ok) {
          const data = await response.json()
          logInfo({ context: CONTEXT }, `Fetched ${data.count} images from backend API`)

          return data.items.map((item: { name: string; path: string; url: string; last_modified?: string }) => {
            // The BFF builds URLs from its internal hostname which may not be
            // reachable from the browser. Replace the origin with BACKEND_API_URL
            // so images load through the same public gateway as API calls.
            let url = item.url
            try {
              const parsed = new URL(item.url)
              url = `${BACKEND_API_URL}${parsed.pathname}${parsed.search}`
            } catch {
              // keep original url if parsing fails
            }
            return {
              id: item.name.replace(/\.[^.]+$/, '').replace(/-/g, '_'),
              name: item.name,
              path: item.path,
              url,
              createdAt: item.last_modified || '',
            }
          })
        } else {
          logWarn({ context: CONTEXT }, `Backend API returned ${response.status}, falling back to predefined list`)
        }
      } catch (error) {
        logError(error as Error, { context: CONTEXT, message: 'Failed to fetch images from backend API, falling back to predefined list' })
      }
    }

    // Fallback to predefined list if backend is not available
    return FALLBACK_BACKGROUND_IMAGES
  },

  async uploadBackgroundImage(file: File): Promise<string> {
    const token = getAccessToken()
    const formData = new FormData()
    formData.append('file', file)

    const response = await fetch(`${BACKEND_API_URL}/api/v1/storage/upload/backgrounds`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    })

    if (!response.ok) {
      const detail = await response.text()
      throw new Error(`Upload failed: ${response.status} ${detail}`)
    }

    const data = await response.json()
    // Rewrite URL origin to use BACKEND_API_URL (same fix as listImages)
    try {
      const parsed = new URL(data.url)
      return `${BACKEND_API_URL}${parsed.pathname}${parsed.search}`
    } catch {
      return data.url
    }
  },
}
